import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DocumentPicker } from './DocumentPicker.js';

interface ArchiveRow {
  readonly archiveId: string;
  readonly archiveTier: string;
  readonly restoreStatus: string;
  readonly archiveStoragePath: string | null;
  readonly archivedAt: string;
}

const TIERS = ['NEARLINE', 'COLD', 'LONG_TERM'];

export function M23ArchiveManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [documentId, setDocumentId] = useState('');
  const [targetTier, setTargetTier] = useState('NEARLINE');
  const [rows, setRows] = useState<ArchiveRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function load(id: string): void {
    setError(null);
    apiRequest<{ data: ArchiveRow[] }>(`/v1/document-archive/by-document/${encodeURIComponent(id)}`, { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  function onLoad(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    load(id);
  }

  function onArchive(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/document-archive/documents/${encodeURIComponent(id)}`, {
      method: 'POST',
      token,
      body: { targetTier },
    })
      .then(() => { setMessage(tr('m23.archive.msgArchived', { tier: targetTier })); load(id); })
      .catch((err) => setError((err as Error).message));
  }

  return (
    <main aria-label="archive-management">
      <h1>{tr('m23.archive.title')}</h1>
      <form aria-label="archive-load-form" onSubmit={onLoad}>
        <DocumentPicker label={tr('m23.archive.documentId')} value={documentId} onPick={(id) => setDocumentId(id)} />
        <button type="submit">{tr('m23.archive.loadHistory')}</button>
      </form>
      <form aria-label="archive-action-form" onSubmit={onArchive}>
        <label htmlFor="targetTier">{tr('m23.archive.targetTier')}</label>
        <select id="targetTier" value={targetTier} onChange={(e) => setTargetTier(e.target.value)}>
          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="submit">{tr('m23.archive.archiveDocument')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {rows && (
        <table aria-label="archive-table">
          <thead><tr><th>{tr('m23.archive.colTier')}</th><th>{tr('m23.archive.colRestoreStatus')}</th><th>{tr('m23.archive.colStoragePath')}</th><th>{tr('m23.archive.colArchivedAt')}</th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.archiveId}>
                <td>{a.archiveTier}</td>
                <td>{a.restoreStatus}</td>
                <td>{a.archiveStoragePath ?? '—'}</td>
                <td>{at(a.archivedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
